/**
 * Auth Modal
 *
 * Modal dialog for login/signup redirecting to central SSO
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Login as LoginIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { getCentralAppUrl } from '../../config/authDomain';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  mode?: 'login' | 'signup';
}

export const AuthModal: React.FC<AuthModalProps> = ({ open, onClose, mode = 'login' }) => {
  const centralAppUrl = getCentralAppUrl();

  const handleRedirectToSSO = (authMode: 'login' | 'signup') => {
    if (!centralAppUrl) return;
    // Same as the sign-in gate: return through /sync-session so the token in the
    // URL is actually read. Sending them to the bare origin drops it.
    const returnTo = window.location.pathname + window.location.search;
    const redirectUrl = `${window.location.origin}/sync-session?returnTo=${encodeURIComponent(returnTo)}`;
    const ssoUrl = `${centralAppUrl}/${authMode}?redirectTo=${encodeURIComponent(redirectUrl)}`;
    window.location.href = ssoUrl;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LoginIcon color="primary" />
            <Typography variant="h6" component="span">
              {mode === 'login' ? 'Sign In' : 'Sign Up'}
            </Typography>
          </Box>
          <IconButton edge="end" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" paragraph>
          {mode === 'login'
            ? 'Sign in with your Coral Creator account to access all features.'
            : 'Create a Coral Creator account to get started with AI-powered image editing.'}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          You'll be redirected to our secure authentication service.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {centralAppUrl ? (
          // Both flows are central-app redirects; surface them together so the
          // user can sign in or create an account from one place.
          <>
            <Button
              variant={mode === 'signup' ? 'contained' : 'outlined'}
              onClick={() => handleRedirectToSSO('signup')}
            >
              Create account
            </Button>
            <Button
              variant={mode === 'signup' ? 'outlined' : 'contained'}
              onClick={() => handleRedirectToSSO('login')}
              startIcon={<LoginIcon />}
            >
              Sign In
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            component={RouterLink}
            to="/dev-auth-helper"
            startIcon={<LoginIcon />}
            onClick={onClose}
          >
            Paste a Token
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
